import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Trophy, MapPin, Languages, ArrowRight, Loader2, Code2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SpotlightBanner from "@/components/SpotlightBanner";
import { getFirmsDirectory, getFirmsLeaderboard } from "@/lib/api";

const TIER_COLOR = {
    starter: "border-white/40 text-white/70",
    professional: "border-[#FFCC00] text-[#FFCC00]",
    elite: "border-[#FF3B30] text-[#FF3B30]",
    enterprise: "border-white text-white",
};

export default function Firms() {
    const [dir, setDir] = useState({ firms: [], total: 0 });
    const [lb, setLb] = useState({ leaderboard: [] });
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ state: "", speciality: "", language: "" });

    useEffect(() => {
        document.title = "Vetted Civil-Rights Firms · Higher Power Movement";
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const [d, l] = await Promise.all([
                getFirmsDirectory({
                    ...(filters.state ? { state: filters.state } : {}),
                    ...(filters.speciality ? { speciality: filters.speciality } : {}),
                    ...(filters.language ? { language: filters.language } : {}),
                    limit: 60,
                }),
                getFirmsLeaderboard(10),
            ]);
            setDir(d);
            setLb(l);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.state, filters.speciality, filters.language]);

    return (
        <div className="bg-[#0a0a0a] min-h-screen text-white">
            <Header />

            {/* HERO */}
            <section className="border-b-2 border-white/20" data-testid="firms-hero">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-16">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00] mb-4">
                        VETTED · STATE-LICENSED · ON-CONTINGENCY
                    </div>
                    <h1 className="font-anton uppercase leading-[0.9] text-5xl sm:text-7xl md:text-8xl">
                        The firms that <span className="text-[#FF3B30]">level the system</span>.
                    </h1>
                    <p className="mt-6 max-w-2xl font-sans text-white/75 text-base sm:text-lg leading-relaxed">
                        Real attorneys. Real wins. Real states. Every firm in this directory has
                        claimed at least one HPM-matched case — that means they've been screened,
                        accepted leads on contingency, and shown up.
                    </p>
                    <div className="flex flex-wrap gap-3 mt-8">
                        <Link to="/hpm-law-firms" className="btn-brutal btn-brutal-red" data-testid="firms-join-cta">
                            ADD YOUR FIRM — 2 FREE LEADS <ArrowRight size={14} />
                        </Link>
                        <Link to="/hpm-widget" className="btn-brutal" data-testid="firms-widget-cta">
                            <Code2 size={14} /> EMBED THE LEAD WIDGET
                        </Link>
                    </div>
                </div>
            </section>

            {/* LEADERBOARD */}
            <section className="border-b-2 border-white/20" data-testid="firms-leaderboard-section">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
                    {/* Spotlight slot — homepage */}
                    <div className="mb-8">
                        <SpotlightBanner slot="homepage" label="FIRM OF THE MONTH" />
                    </div>
                    <div className="flex items-center gap-3 mb-2">
                        <Trophy size={18} className="text-[#FFCC00]" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00]">
                            TOP 10 · MOST LEADS CLAIMED
                        </span>
                    </div>
                    <h2 className="font-anton text-4xl sm:text-5xl uppercase mb-6">
                        Who's showing up the most?
                    </h2>
                    {lb.leaderboard.length === 0 ? (
                        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/50">
                            LEADERBOARD UNLOCKS AT 3+ CLAIMS · BE FIRST.
                        </div>
                    ) : (
                        <div className="border-2 border-white/20">
                            {lb.leaderboard.map((f, i) => (
                                <div
                                    key={f.firm_id}
                                    className="p-4 [&:not(:last-child)]:border-b-2 border-white/20 flex items-center gap-4"
                                    data-testid={`leaderboard-row-${i}`}
                                >
                                    <div className="font-anton text-4xl text-[#FFCC00] w-12 flex-shrink-0 text-center">
                                        {String(i + 1).padStart(2, "0")}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-anton text-xl uppercase truncate">
                                            {f.firm_name}
                                        </div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mt-1">
                                            {(f.states || []).join(" · ") || "MULTI-STATE"} · TIER{" "}
                                            {(f.tier || "starter").toUpperCase()}
                                        </div>
                                    </div>
                                    <div className="font-mono text-xs text-[#FF3B30] whitespace-nowrap">
                                        {f.leads_claimed_total} CLAIMED
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* DIRECTORY + FILTERS */}
            <section>
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
                    <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] mb-2">
                                DIRECTORY
                            </div>
                            <h2 className="font-anton text-4xl uppercase">
                                Find your firm.
                            </h2>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <input
                                placeholder="State"
                                value={filters.state}
                                onChange={(e) => setFilters((s) => ({ ...s, state: e.target.value }))}
                                className="bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-xs text-white placeholder-white/40 outline-none focus:border-white"
                                data-testid="filter-state"
                            />
                            <input
                                placeholder="Speciality"
                                value={filters.speciality}
                                onChange={(e) => setFilters((s) => ({ ...s, speciality: e.target.value }))}
                                className="bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-xs text-white placeholder-white/40 outline-none focus:border-white"
                                data-testid="filter-speciality"
                            />
                            <input
                                placeholder="Language"
                                value={filters.language}
                                onChange={(e) => setFilters((s) => ({ ...s, language: e.target.value }))}
                                className="bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-xs text-white placeholder-white/40 outline-none focus:border-white"
                                data-testid="filter-language"
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="py-16 text-center">
                            <Loader2 size={28} className="animate-spin mx-auto text-[#FF3B30]" />
                        </div>
                    ) : dir.firms.length === 0 ? (
                        <div className="border-2 border-dashed border-white/20 p-12 text-center">
                            <div className="font-anton text-2xl uppercase">No firms matching those filters.</div>
                            <p className="font-sans text-white/60 mt-2 text-sm">
                                Be the first — add your firm and get 2 free leads.
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-2 border-white/20">
                            {dir.firms.map((f, i) => (
                                <div
                                    key={f.firm_id}
                                    className="p-5 border-white/20 [&:not(:nth-child(3n))]:lg:border-r-2 [&:not(:last-child)]:border-b-2 lg:[&:nth-last-child(-n+3)]:border-b-0"
                                    data-testid={`firm-card-${i}`}
                                >
                                    <div className="flex items-start justify-between gap-3 mb-2">
                                        <h3 className="font-anton text-xl uppercase leading-tight">
                                            {f.firm_name}
                                        </h3>
                                        <span
                                            className={`font-mono text-[9px] uppercase tracking-[0.25em] px-2 py-1 border-2 whitespace-nowrap ${TIER_COLOR[f.tier] || TIER_COLOR.starter}`}
                                        >
                                            <Crown size={9} className="inline mr-1" />{" "}
                                            {(f.tier || "starter").toUpperCase()}
                                        </span>
                                    </div>
                                    {(f.states || []).length > 0 && (
                                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 flex items-center gap-1 mt-2">
                                            <MapPin size={10} /> {(f.states || []).slice(0, 4).join(" · ")}
                                        </div>
                                    )}
                                    {(f.languages || []).length > 0 && (
                                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 flex items-center gap-1 mt-1">
                                            <Languages size={10} /> {(f.languages || []).slice(0, 4).join(" · ")}
                                        </div>
                                    )}
                                    {(f.specialities || []).length > 0 && (
                                        <div className="mt-3 flex flex-wrap gap-1">
                                            {(f.specialities || []).slice(0, 4).map((s) => (
                                                <span
                                                    key={s}
                                                    className="font-mono text-[9px] uppercase tracking-wider px-2 py-1 border border-white/20 text-white/60"
                                                >
                                                    {s.replace(/_/g, " ")}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF3B30]">
                                        {f.leads_claimed_total || 0} LEADS CLAIMED
                                        {f.on_trial && <span className="ml-2 text-[#FFCC00]">· ON TRIAL</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="mt-8 text-center">
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">
                            DON'T SEE YOUR FIRM? JOIN THE MOVEMENT.
                        </p>
                        <Link to="/hpm-law-firms" className="btn-brutal btn-brutal-red">
                            START FREE — 2 LEADS ON US <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>

            <Footer disclaimer="" />
        </div>
    );
}
